import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import { SoftwarePackagesIcon } from "components/icons/iconComponents";
import { Box } from "@mui/material";
import LoadingSpinner from "components/atomics/LoadingSpinner";
import MainLayout from "components/templates/MainLayout";
import { ErrorMessage } from "globalErrorhandling/ErrorPage";
import Configurator from "raft/configurator";
import { ComponentType, lazy, Suspense } from "react";
import { Navigate } from "react-router-dom";
import translations from "translations";
import { Route } from "./types";
import { getDisabledFeatures, type FeatureKey } from "authentication/runtimeConfig";
import {
  BoxIcon,
  CheckListIcon,
  CollageIcon,
  IoTDevice,
  PeopleIcon,
  SecurityIcon,
  SettingsIcon,
} from "@mlpa/core-ui-core-open_ocean_ui";
import { AppsIcon } from "@mlpa/core-ui-core-open_ocean_ui/Atoms/Icons/Inonicool/custom";

const Home = () => import("pages/Home");
const AssetManagement = () => import("pages/AssetManagement");
const Assets = () => import("pages/AssetManagement/Assets");
const AssetDetails = () => import("pages/AssetManagement/Assets/AssetOverview/AssetDetails");
const AssetCreateForm = () => import("pages/AssetManagement/Assets/AssetForms/CreateAsset");
const EditAssetForm = () => import("pages/AssetManagement/Assets/AssetForms/EditAsset");
const AssetTypes = () => import("pages/AssetManagement/AssetTypes");
const AssetTypeCreate = () => import("pages/AssetManagement/AssetTypes/AssetTypeMutations/AssetTypeCreate");
const AssetTypeUpdate = () => import("pages/AssetManagement/AssetTypes/AssetTypeMutations/AssetTypeUpdate");
const AssetModelCreate = () =>
  import("pages/AssetManagement/AssetTypes/AssetModels/AssetModelMutations/AssetModelCreate");
const AssetModelUpdate = () =>
  import("pages/AssetManagement/AssetTypes/AssetModels/AssetModelMutations/AssetModelUpdate");
const AssetModelDetails = () => import("pages/AssetManagement/AssetTypes/AssetModels/AssetModelDetails");
const DevicesManagement = () => import("pages/DevicesManagement");
const DeviceTypes = () => import("pages/DevicesManagement/DeviceTypes");
const DeviceModelDetails = () => import("pages/DevicesManagement/DeviceTypes/DeviceModelDetails");
const DeviceOverview = () => import("pages/DevicesManagement/Devices");
const DeviceCreateForm = () => import("pages/DevicesManagement/Devices/DeviceForms/CreateDevice");
const DeviceActivateForm = () => import("pages/DevicesManagement/Devices/DeviceForms/ActivateDevice");
const EditDeviceForm = () => import("pages/DevicesManagement/Devices/DeviceForms/EditDevice");
const DeploymentGroups = () => import("pages/DevicesManagement/DeploymentGroups");
const SoftwareManagement = () => import("pages/SoftwareManagement");
const SoftwarePackages = () => import("pages/SoftwareManagement/SoftwarePackages");
const SoftwarePackagesOverview = () => import("pages/SoftwareManagement/SoftwarePackages/SoftwarePackagesOverview");
const DeviceDetail = () => import("pages/DevicesManagement/DeviceDetail");
const AssetAttributesSets = () => import("pages/AssetManagement/AttributeSetsAndfields");
const CreateDeploymentGroup = () => import("pages/DevicesManagement/DeploymentGroups/CreateDeploymentGroup");
const UpdateDeploymentGroup = () => import("pages/DevicesManagement/DeploymentGroups/UpdateDeploymentGroup");
const Apps = () => import("pages/SoftwareManagement/Apps");
const NewReleased = () => import("pages/SoftwareManagement/NewReleased");
const DeniedSoftware = () => import("pages/SoftwareManagement/NewReleased/DeniedSoftware");
const SoftwareToReview = () => import("pages/SoftwareManagement/NewReleased/SoftwareToReview");
const SoftwareReviewDetails = () => import("pages/SoftwareManagement/NewReleased/SoftwareReviewDetails");
const UserManagement = () => import("pages/UserManagement");
const UserAndRolesTable = () => import("pages/UserManagement/UserAndRolesTable");
const MappingRolesUserUpdate = () => import("pages/UserManagement/MappingRolesUserUpdate");
const UserProfile = () => import("pages/UserProfile");
const UserRolesManager = () => import("pages/UserManagement/UserRolesManager");
const UpdateRole = () => import("pages/UserManagement/UpdateRole");
const SecurityCenter = () => import("pages/SecurityCenter");
const SecurityDashboard = () => import("pages/SecurityCenter/Dashboard/SecurityDashboard");
const VulnerabilityList = () => import("pages/SecurityCenter/VulnerabilityList/VulnerabilityList");
const VulnerabilityDetail = () => import("pages/SecurityCenter/VulnerabilityDetail/VulnerabilityDetail");
const SharedDeviceModels = () => import("pages/DevicesManagement/SharedDeviceModels");

//TODO #6321 FM @next be version

const AsyncRoute = (ElementPre: () => Promise<{ default: ComponentType }>) => {
  const waiter = (time: number, fail = false) =>
    new Promise((resolve, reject) => {
      setTimeout(() => {
        if (fail) {
          reject(false);
        } else {
          resolve(true);
        }
      }, time);
    });
  const Element = async () => {
    await waiter(50);
    const El = await ElementPre();
    return El;
  };

  const Page = lazy(Element);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        overflow: "visible",
        maxHeight: "100%",
        width: "100%",
        maxWidth: "100%",
        flex: 1,
      }}
    >
      <Suspense fallback={<LoadingSpinner loading />}>
        <Page />
      </Suspense>
    </Box>
  );
};

const routes = (): Route[] => {
  const routesArray: Route[] = [
    {
      path: "",
      element: <MainLayout />,
      label: "",
      menuItem: false,
      mainLayout: false,
      scope: "Home",
      notification: true,
      errorElement: ErrorMessage,
      children: [
        {
          path: "/",
          element: <Navigate to="/assetManagement" />,
          label: "Home",
          menuItem: false,
          mainLayout: true,
          scope: "Home",
          notification: true,
        },
        {
          path: "assetManagement",
          element: AsyncRoute(AssetManagement),
          label: translations.pages.assetManagement.label,
          menuItem: true,
          menuSection: true,
          mainLayout: true,
          addBreadcrumb: false,
          menuIcon: ({ selected }) => <BoxIcon hoverBold selected={selected} />,
          scope: "Home",
          notification: false,
          handle: {
            label: translations.pages.assetManagement.label,
            childLinks: [
              { To: "", label: translations.pages.assetManagement.tabs.assets.assetList },
              { To: "assetTypes", label: translations.pages.assetManagement.tabs.assetTypes.label },
              {
                To: "assetAttributeFieldsAndSets",
                label: translations.pages.assetManagement.tabs.assetAttributeFieldsAndSets.label,
              },
            ],
          },
          children: [
            {
              path: "",
              element: AsyncRoute(Assets),
              label: translations.pages.assetManagement.tabs.assets.label,
              layout: "mainOverview",
              menuItem: true,
              mainLayout: true,
              menuIcon: ({ selected }) => <BoxIcon hoverBold selected={selected} />,
              scope: "Home",
              notification: false,
              /**
               * handle.pageTitle - Used to display a consistent page title in the browser tab or header
               *
               * Why we use this:
               * - Child routes (like createAsset, editAsset, assetDetails) need to show the PARENT section's title
               *   in the page header/breadcrumb, not their own specific label
               * - Example: When on "/assetManagement/createAsset", we want the header to show "Assets" (the main section)
               *   not "New Asset" (the specific page label)
               * - This separates the navigation label (used in menus/breadcrumbs) from the page title (shown in header)
               * - The `label` property is for breadcrumb/menu navigation
               * - The `handle.pageTitle` is for the main page header display
               *
               * This pattern ensures consistent UX where users always see which main section they're in,
               * even when navigating to sub-pages like create/edit/details forms.
               */
              handle: {
                pageTitle: translations.pages.assetManagement.tabs.assets.label,
              },

              children: [
                {
                  path: "createAsset",
                  element: AsyncRoute(AssetCreateForm),
                  label: translations.pages.assetManagement.tabs.assets.newAsset,
                  addBreadcrumb: true,
                  menuItem: true,
                  layout: "fullscreen",
                  mainLayout: true,
                  handle: {
                    pageTitle: translations.pages.assetManagement.tabs.assets.label,
                    hideTabs: true,
                  },
                  //menuIcon: ({ color }) => <TrainOutlinedIcon htmlColor={color} />,
                  scope: "Asset.Update",
                  // scope needs to be changed to asset.create once provided by backend
                  notification: false,
                  children: [],
                },
                {
                  path: "editAsset/:assetId",
                  element: AsyncRoute(EditAssetForm),
                  menuItem: false,
                  label: translations.pages.assetManagement.tabs.assets.editAsset,
                  layout: "fullscreen",
                  notification: false,
                  handle: {
                    pageTitle: translations.pages.assetManagement.tabs.assets.label,
                    hideTabs: true,
                  },
                  mainLayout: true,
                  scope: "Home",
                  linkId: "editAsset",
                },
                {
                  path: ":assetId",
                  element: AsyncRoute(AssetDetails),
                  menuItem: false,
                  addBreadcrumb: true,
                  label: translations.globals.headlines.asset_details,
                  notification: false,
                  mainLayout: true,
                  handle: {
                    pageTitle: translations.pages.assetManagement.tabs.assets.label,
                  },
                  layout: "detail",
                  scope: "Home",
                  linkId: "assetDetails",
                },
              ],
            },
            {
              path: "assetTypes",
              element: AsyncRoute(AssetTypes),
              label: translations.pages.assetManagement.tabs.assets.label,
              menuItem: false,
              mainLayout: true,
              layout: "previewPane",
              menuIcon: ({ selected }) => <BoxIcon hoverBold selected={selected} />,
              scope: "Home",
              notification: false,
              children: [
                {
                  path: "newAssetType",
                  element: AsyncRoute(AssetTypeCreate),
                  menuItem: false,
                  label: translations.pages.assetManagement.tabs.assetTypes.newAssetType,
                  notification: false,
                  handle: {
                    pageTitle: translations.pages.assetManagement.tabs.assets.label,
                    hideTabs: true,
                  },
                  layout: "fullscreen",
                  mainLayout: true,
                  scope: "Home",
                },
                {
                  path: "updateAssetType/:assetTypeId",
                  element: AsyncRoute(AssetTypeUpdate),
                  menuItem: false,
                  label: translations.pages.assetManagement.tabs.assetTypes.editAssetType,
                  addBreadcrumb: true,
                  layout: "fullscreen",
                  notification: false,
                  mainLayout: true,
                  scope: "Home",
                  handle: {
                    pageTitle: translations.pages.assetManagement.tabs.assets.label,
                    hideTabs: true,
                  },
                },
                {
                  path: "createAssetModel",
                  element: AsyncRoute(AssetModelCreate),
                  menuItem: false,
                  label: translations.pages.assetManagement.tabs.assetTypes.tabs.models.newAssetModel,
                  layout: "fullscreen",
                  notification: false,
                  mainLayout: true,
                  handle: {
                    pageTitle: translations.pages.assetManagement.tabs.assets.label,
                    hideTabs: true,
                  },
                  scope: "Home",
                },
                {
                  path: "updateAssetModel/:assetModelId",
                  element: AsyncRoute(AssetModelUpdate),
                  menuItem: false,
                  label: translations.pages.assetManagement.tabs.assetTypes.tabs.models.updateAssetModel,
                  layout: "fullscreen",
                  notification: false,
                  handle: {
                    pageTitle: translations.pages.assetManagement.tabs.assets.label,
                    hideTabs: true,
                  },
                  mainLayout: true,
                  scope: "Home",
                },
                {
                  path: "assetModelDetails/:assetModelId",
                  element: AsyncRoute(AssetModelDetails),
                  menuItem: false,
                  label: translations.pages.assetManagement.tabs.assetTypes.tabs.models.assetModelDetailsView,
                  handle: {
                    pageTitle: translations.pages.assetManagement.tabs.assets.label,
                  },
                  layout: "detail",
                  notification: false,
                  mainLayout: true,
                  scope: "Home",
                },
              ],
            },
            {
              path: "assetAttributeFieldsAndSets",
              element: AsyncRoute(AssetAttributesSets),
              label: translations.pages.assetManagement.tabs.assets.label,
              menuItem: false,
              mainLayout: true,
              layout: "previewPane",
              menuIcon: ({ selected }) => <BoxIcon hoverBold selected={selected} />,
              scope: "Home",
              notification: false,
              children: [],
            },
          ],
        },
        {
          path: "deviceManagement",
          element: AsyncRoute(DevicesManagement),
          label: translations.pages.deviceManagement.label,
          menuItem: true,
          menuSection: true,
          mainLayout: true,
          addBreadcrumb: false,
          menuIcon: ({ selected }) => <IoTDevice hoverBold selected={selected} />,
          scope: "Home",
          notification: false,
          handle: {
            label: translations.pages.deviceManagement.label,
            childLinks: [
              { To: "", label: translations.pages.deviceManagement.tabs.devices.deviceList },
              { To: "deviceTypes", label: translations.pages.deviceManagement.tabs.deviceTypes.label },
              { To: "deploymentGroups", label: translations.pages.deviceManagement.tabs.deploymentGroups.label },
              {
                To: "sharedDeviceModels",
                label: translations.pages.deviceManagement.tabs.sharedModels.label,
              },
            ],
          },
          children: [
            {
              path: "",
              element: AsyncRoute(DeviceOverview),
              layout: "mainOverview",
              label: translations.pages.deviceManagement.tabs.devices.label,
              menuItem: true,
              mainLayout: true,
              menuIcon: ({ selected }) => <IoTDevice hoverBold selected={selected} />,
              scope: "Home",
              notification: false,
              children: [
                {
                  path: "deviceDetail/:deviceId",
                  element: AsyncRoute(DeviceDetail),
                  menuItem: false,
                  label: translations.pages.deviceManagement.tabs.devices.label,
                  addBreadcrumb: false,
                  notification: false,
                  mainLayout: true,
                  layout: "detail",
                  scope: "Home",
                  linkId: "deviceDetail",
                },
                {
                  path: "createDevice",
                  element: AsyncRoute(DeviceCreateForm),
                  label: translations.pages.deviceManagement.tabs.devices.newDevice,
                  menuItem: true,
                  handle: {
                    pageTitle: translations.pages.deviceManagement.tabs.devices.label,
                    hideTabs: true,
                  },
                  mainLayout: true,
                  layout: "fullscreen",
                  scope: "Device.Create",
                  notification: false,
                  children: [],
                },
                {
                  path: "activateDevice",
                  element: AsyncRoute(DeviceActivateForm),
                  label: translations.pages.deviceManagement.tabs.devices.activateDevice.label,
                  menuItem: true,
                  handle: {
                    pageTitle: translations.pages.deviceManagement.tabs.devices.label,
                    hideTabs: true,
                  },
                  mainLayout: true,
                  layout: "fullscreen",
                  scope: "DeviceProvisioningClaim.Create.Tenant",
                  notification: false,
                  featureKey: "deviceClaiming",
                  children: [],
                },
                {
                  path: "editDevice/:assetId",
                  element: AsyncRoute(EditDeviceForm),
                  menuItem: false,
                  label: translations.pages.deviceManagement.tabs.devices.editDevice,
                  handle: {
                    pageTitle: translations.pages.deviceManagement.tabs.devices.label,
                    hideTabs: true,
                  },
                  notification: false,
                  mainLayout: true,
                  layout: "fullscreen",

                  scope: "Home",
                  linkId: "editDevice",
                },
                {
                  path: "edgeDeviceDetail/:deviceId",
                  element: AsyncRoute(DeviceDetail),
                  menuItem: false,
                  label: translations.pages.deviceManagement.tabs.deviceDetails.label,
                  handle: {
                    pageTitle: translations.pages.deviceManagement.tabs.devices.label,
                    hideTabs: true,
                  },
                  notification: false,
                  layout: "detail",
                  mainLayout: true,
                  scope: "Home",
                },
              ],
            },
            {
              path: "deviceTypes",
              element: AsyncRoute(DeviceTypes),
              label: translations.pages.deviceManagement.tabs.devices.label,
              menuItem: false,
              mainLayout: true,
              layout: "previewPane",
              menuIcon: ({ color }) => <AccountTreeOutlinedIcon htmlColor={color} />,
              scope: "Home",
              notification: false,
              children: [
                {
                  path: "deviceModelDetails/:deviceModelId",
                  element: AsyncRoute(DeviceModelDetails),
                  menuItem: false,
                  label: translations.entities.devices.deviceTypeDetails,
                  handle: {
                    pageTitle: translations.pages.deviceManagement.tabs.devices.label,
                  },
                  layout: "detail",
                  notification: false,
                  mainLayout: true,
                  scope: "Home",
                },
              ],
            },
            {
              path: "deploymentGroups",
              element: AsyncRoute(DeploymentGroups),
              label: translations.pages.deviceManagement.tabs.devices.label,
              menuItem: false,
              layout: "previewPane",
              mainLayout: true,
              menuIcon: ({ color }) => <FactCheckOutlinedIcon htmlColor={color} />,
              scope: "Home",
              notification: false,
              linkId: "deploymentGroups",
              children: [
                {
                  path: "newDeploymentGroup",
                  element: AsyncRoute(CreateDeploymentGroup),
                  menuItem: false,
                  layout: "fullscreen",
                  handle: {
                    pageTitle: translations.pages.deviceManagement.tabs.devices.label,
                    hideTabs: true,
                  },
                  label: translations.pages.deviceManagement.tabs.deploymentGroups.newDeploymentGroup,
                  notification: false,
                  mainLayout: true,
                  scope: "Home",
                },
                {
                  path: "editDeploymentGroup",
                  element: AsyncRoute(UpdateDeploymentGroup),
                  menuItem: false,
                  layout: "fullscreen",
                  handle: {
                    pageTitle: translations.pages.deviceManagement.tabs.devices.label,
                    hideTabs: true,
                  },
                  label: translations.pages.deviceManagement.tabs.deploymentGroups.updateDeploymentGroup,
                  notification: false,
                  mainLayout: true,
                  scope: "Home",
                },
              ],
            },
            {
              path: "sharedDeviceModels",
              element: AsyncRoute(SharedDeviceModels),
              label: translations.pages.deviceManagement.tabs.sharedModels.label,
              menuItem: false,
              mainLayout: true,
              layout: "mainOverview",
              scope: "Home",
              notification: false,
            },
          ],
        },

        {
          path: "",
          element: AsyncRoute(SoftwarePackages),
          label: translations.pages.softwareManagement.label,
          menuItem: true,
          menuSection: true,
          mainLayout: true,
          scope: "SoftwareManagement",
          notification: false,
          addBreadcrumb: false,
          children: [
            {
              path: "softwareManagement",
              element: AsyncRoute(Apps),
              label: translations.pages.softwareManagement.tabs.apps.label,
              layout: "previewPane",
              menuItem: true,
              mainLayout: true,
              menuIcon: ({ selected }) => <AppsIcon hoverBold selected={selected} />,
              scope: "Home",
              notification: false,
              featureKey: "!softwarePackages",
            },
            {
              path: "softwarePackages",
              element: AsyncRoute(SoftwarePackagesOverview),
              label: translations.pages.softwareManagement.tabs.softwarePackages.label,
              layout: "mainOverview",
              menuItem: true,
              mainLayout: true,
              menuIcon: ({ color }) => <SoftwarePackagesIcon color={color} />,
              scope: "Home",
              notification: false,
              featureKey: "softwarePackages",
            },
            {
              path: "softwarePackages/:id",
              element: AsyncRoute(Apps),
              label: translations.pages.softwareManagement.tabs.softwarePackages.label,
              layout: "previewPane",
              menuItem: false,
              mainLayout: true,
              scope: "Home",
              notification: false,
              featureKey: "softwarePackages",
            },
            {
              path: "softwareReview",
              element: AsyncRoute(NewReleased),
              label: translations.pages.softwareManagement.tabs.newReleases.label,
              menuItem: true,
              mainLayout: true,
              layout: "mainOverview",
              menuIcon: ({ selected }) => <CheckListIcon hoverBold selected={selected} />,
              scope: "Home",
              notification: false,
              handle: {
                childLinks: [
                  { To: "", label: translations.pages.softwareManagement.tabs.newReleases.softwareToReview },
                  {
                    To: "deniedSoftware",
                    label: translations.pages.softwareManagement.tabs.newReleases.deniedSoftware,
                  },
                ],
              },
              children: [
                {
                  path: "",
                  element: AsyncRoute(SoftwareToReview),
                  label: translations.pages.softwareManagement.tabs.newReleases.label,
                  menuItem: false,
                  mainLayout: true,
                  scope: "Home",
                  notification: false,
                  addBreadcrumb: false,
                  children: [
                    {
                      path: "softwareToReviewDetails",
                      element: AsyncRoute(SoftwareReviewDetails),
                      label: "",
                      layout: "detail",
                      menuItem: false,
                      mainLayout: true,
                      scope: "Home",
                      notification: false,
                      handle: {
                        pageTitle: translations.pages.softwareManagement.tabs.newReleases.label,
                      },
                    },
                  ],
                },
                {
                  path: "deniedSoftware",
                  addBreadcrumb: false,
                  element: AsyncRoute(DeniedSoftware),
                  label: translations.pages.softwareManagement.tabs.newReleases.label,
                  menuItem: false,
                  mainLayout: true,
                  scope: "Home",
                  notification: false,
                },
              ],
            },
          ],
        },
        {
          path: "/securityCenter",
          element: AsyncRoute(SecurityCenter),
          label: translations.pages.securityCenter.label,
          menuItem: true,
          menuSection: true,
          mainLayout: true,
          scope: "Home",
          addBreadcrumb: false,
          notification: false,
          featureKey: "cveManagement",
          children: [
            {
              path: "",
              element: AsyncRoute(SecurityDashboard),
              label: translations.pages.securityCenter.tabs.dashboard.label,
              layout: "mainOverview",
              menuItem: true,
              mainLayout: true,
              menuIcon: ({ selected }) => <CollageIcon hoverBold selected={selected} />,
              scope: "Home",
              notification: false,
            },
            {
              path: "vulnerabilities",
              element: AsyncRoute(VulnerabilityList),
              label: translations.pages.securityCenter.tabs.vulnerabilities.label,
              layout: "mainOverview",
              menuItem: true,
              mainLayout: true,
              menuIcon: ({ selected }) => <SecurityIcon hoverBold selected={selected} />,
              scope: "Home",
              notification: false,
            },
            {
              path: "vulnerabilities/:id",
              element: AsyncRoute(VulnerabilityDetail),
              menuItem: false,
              label: translations.pages.securityCenter.tabs.vulnerabilities.label,
              layout: "detail",
              notification: false,
              mainLayout: true,
              scope: "Home",
              linkId: "vulnerabilityDetail",
            },
          ],
        },
        {
          path: "/userManagement",
          element: AsyncRoute(UserManagement),
          menuItem: true,
          menuSection: true,
          addBreadcrumb: true,
          label: translations.entities.user.plural,
          mainLayout: true,
          layout: "mainOverview",
          menuIcon: ({ selected }) => <PeopleIcon hoverBold selected={selected} />,
          scope: "Home",
          notification: false,
          featureKey: "userManagement",
          children: [
            {
              path: "",
              element: AsyncRoute(UserAndRolesTable),
              label: translations.entities.user.plural,
              menuItem: true,
              mainLayout: true,
              addBreadcrumb: false,
              menuIcon: ({ selected }) => <PeopleIcon hoverBold selected={selected} />,
              scope: "Home",
              notification: false,
            },
            {
              path: "userRolesManager",
              element: AsyncRoute(UserRolesManager),
              label: translations.pages.userManagement.userRoleManager.label,
              mainLayout: true,
              layout: "detail",
              handle: {
                pageTitle: translations.entities.user.plural,
              },
              menuItem: false,
              scope: "Settings",
              notification: false,
              children: [],
            },
            {
              path: "userRolesManager/updateRole/:roleId",
              element: AsyncRoute(UpdateRole),
              label: translations.pages.userManagement.form.label.role,
              mainLayout: true,
              handle: {
                pageTitle: translations.entities.user.plural,
              },
              layout: "detail",
              menuItem: false,
              scope: "Settings",
              notification: false,
              children: [],
            },
          ],
        },
        {
          path: "assignRolesToUser/:userId",
          element: AsyncRoute(MappingRolesUserUpdate),
          scope: "Hidden",
          label: translations.pages.userManagement.assignRolesToUser.label,
          mainLayout: true,
          layout: "detail",
          menuItem: false,

          notification: false,
        },
        {
          path: "/myProfile",
          element: AsyncRoute(UserProfile),
          label: translations.entities.user.columns.myProfile,
          menuItem: false,
          mainLayout: false,
          layout: "mainOverview",
          scope: "Home",
          notification: false,
          children: [],
        },
      ],
    },
  ];
  if (import.meta.env.MODE === "development") {
    routesArray[0]?.children?.push({
      path: "configurator",
      element: AsyncRoute(() => Promise.resolve({ default: Configurator })),
      menuIcon: ({ selected }) => <SettingsIcon hoverBold selected={selected} />,
      label: "Configurator",
      menuItem: true,
      mainLayout: true,
      scope: "Home",
      notification: false,
    });
  }

  // Filter out routes based on disabled features.
  // featureKey: "someFeature"  → show only when the feature is enabled
  // featureKey: "!someFeature" → show only when the feature is disabled (negation prefix)
  const disabledFeatures = getDisabledFeatures();
  const filterRoutes = (routes: Route[]): Route[] =>
    routes
      .filter((route) => {
        if (!route.featureKey) return true;
        if (route.featureKey.startsWith("!")) {
          const key = route.featureKey.slice(1) as FeatureKey;
          return disabledFeatures.has(key);
        }
        return !disabledFeatures.has(route.featureKey as FeatureKey);
      })
      .map((route) => (route.children ? { ...route, children: filterRoutes(route.children) } : route));
  return filterRoutes(routesArray);
};
export default routes;
